import { useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import type { components } from '@trackercop/contracts';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { PayloadCategoryChip } from '@/components/category-chip';
import type { RecipientTableRow } from '@/lib/site-data-flow-graph';

type PayloadCategory = components['schemas']['PayloadCategory'];
type ConsentMode = components['schemas']['ConsentMode'];

interface RecipientsTableProps {
  rows: RecipientTableRow[];
  onSelectRecipient: (row: RecipientTableRow) => void;
}

const CONSENT_MODE_LABEL: Record<ConsentMode, string> = {
  'no-consent': 'No consent',
  'accept-all': 'Accept all',
  'reject-all': 'Reject all',
};

const recipientsColumns: ColumnDef<RecipientTableRow>[] = [
  {
    id: 'vendor',
    header: 'Recipient',
    accessorFn: (row) => row.vendor.displayName,
    cell: ({ row }) => {
      const cloakedVia = row.original.vendor.cloakedVia ?? null;
      return (
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <span className="font-medium">{row.original.vendor.displayName}</span>
            {cloakedVia && (
              <Badge
                variant="warning"
                title={`Reached via CNAME from ${cloakedVia}`}
                data-testid="recipients-cloaked-badge"
              >
                cloaked
              </Badge>
            )}
          </div>
          <span className="font-mono text-[11px] text-muted-foreground">
            {row.original.vendor.primaryDomain}
          </span>
        </div>
      );
    },
  },
  {
    id: 'pagesContacted',
    header: '# Pages',
    accessorFn: (row) => row.pagesContacted,
    cell: ({ getValue }) => (
      <span className="font-mono text-xs">{Number(getValue())}</span>
    ),
  },
  {
    id: 'totalRequests',
    header: '# Payloads',
    accessorFn: (row) => row.totalRequests,
    cell: ({ getValue }) => (
      <span className="font-mono text-xs">{Number(getValue())}</span>
    ),
  },
  {
    id: 'dominantCategory',
    header: 'Categories',
    // Sort by dominant first; the cell still renders the full set so a
    // reviewer sees what's mixed in.
    accessorFn: (row) => row.dominantCategory,
    cell: ({ row }) => {
      const cats = Array.from(row.original.categories) as PayloadCategory[];
      cats.sort();
      return (
        <div className="flex flex-wrap gap-1">
          {cats.map((c) => (
            <PayloadCategoryChip key={c} category={c} />
          ))}
        </div>
      );
    },
  },
  {
    id: 'consentModes',
    header: 'Consent modes seen',
    accessorFn: (row) => Array.from(row.consentModes).sort().join(','),
    cell: ({ row }) => (
      <span className="text-xs text-muted-foreground">
        {Array.from(row.original.consentModes)
          .map((m) => CONSENT_MODE_LABEL[m])
          .sort()
          .join(', ')}
      </span>
    ),
  },
];

// Recipients table — top-of-page primary answer for "who are we
// sharing data with". Sortable on every column. CLAUDE.md Phase 3a
// pins ~500 rows as the threshold for swapping to a virtualised
// renderer; until profiling shows real-world recipient lists exceed
// that, plain rendering is the right default.
export function RecipientsTable({ rows, onSelectRecipient }: RecipientsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'totalRequests', desc: true },
  ]);

  const table = useReactTable({
    data: rows,
    columns: recipientsColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (rows.length === 0) {
    return (
      <div
        className="rounded-md border bg-background p-6 text-sm text-muted-foreground"
        data-testid="recipients-empty"
      >
        No recipients observed in the latest scan. Trigger a new scan or
        verify the site is reachable.
      </div>
    );
  }

  return (
    <Table>
      <THead>
        {table.getHeaderGroups().map((group) => (
          <TR key={group.id}>
            {group.headers.map((header) => (
              <TH
                key={header.id}
                onClick={header.column.getToggleSortingHandler()}
                className={
                  header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                }
                data-testid={`recipients-header-${header.id}`}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
                {header.column.getIsSorted() === 'asc'
                  ? ' ↑'
                  : header.column.getIsSorted() === 'desc'
                    ? ' ↓'
                    : ''}
              </TH>
            ))}
          </TR>
        ))}
      </THead>
      <TBody>
        {table.getRowModel().rows.map((row) => (
          <TR
            key={row.id}
            data-testid={`recipient-row-${row.original.recipientNodeId}`}
            onClick={() => onSelectRecipient(row.original)}
            className="cursor-pointer"
          >
            {row.getVisibleCells().map((cell) => (
              <TD key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TD>
            ))}
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
